import { useSnackbar } from 'notistack';
import { BaseSyntheticEvent } from 'react';
import { FieldErrors, UseFormReturn } from 'react-hook-form';
import { translateKey } from 'src/locales/translate';
import { CreateReminderDtoRecurrenceTypeEnum } from 'woofwoof-api';
import { isMedicationRecordChanged } from './medicationFormPayload';
import {
  MedicationsFormModalValuesProps,
  withLegacyDosing,
} from './medicationFormSchema';

interface MedicationFormSubmitArgs {
  methods: UseFormReturn<MedicationsFormModalValuesProps>;
  /** What the form opened with, to tell a status-only save from an edit (#707). */
  defaultValues: MedicationsFormModalValuesProps;
  /** `recordChanged`: false when only the status moved. */
  onSubmit: (
    data: MedicationsFormModalValuesProps,
    meta: { recordChanged: boolean },
  ) => void;
  singleReminderOnly: boolean;
  /**
   * R3-4: the host's write series is unresolved. The submit skips validation
   * and step navigation: the host resends its frozen request and ignores the
   * values it is handed.
   */
  unconfirmedSave: boolean;
  goToFirstError: (
    errors: FieldErrors<MedicationsFormModalValuesProps>,
  ) => void;
}

/** The medication form's `onSubmit`: validated, or a resend of an unconfirmed save. */
export function useMedicationFormSubmit({
  methods,
  defaultValues,
  onSubmit,
  singleReminderOnly,
  unconfirmedSave,
  goToFirstError,
}: MedicationFormSubmitArgs): (event?: BaseSyntheticEvent) => Promise<void> {
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, getValues } = methods;
  const submit = (values: MedicationsFormModalValuesProps): void =>
    onSubmit(
      withLegacyDosing(
        singleReminderOnly
          ? {
              ...values,
              reminderRecurrenceType:
                CreateReminderDtoRecurrenceTypeEnum.Single,
            }
          : values,
      ),
      {
        recordChanged: isMedicationRecordChanged(getValues(), defaultValues),
      },
    );

  if (unconfirmedSave) {
    return async (event) => {
      event?.preventDefault();
      submit(getValues());
    };
  }
  return handleSubmit(
    submit,
    // A rejected form used to do nothing visible beyond the field helper
    // text, which off-screen reads as a save that vanished. With the fields
    // spread over steps, the offending one can be on a step the vet is not
    // looking at — so go there.
    (errors) => {
      goToFirstError(errors);
      enqueueSnackbar(translateKey('medication.form.invalidForm'), {
        variant: 'warning',
      });
    },
  );
}
